// Segment Learning Center (LMS) progress events — ported from hec-composable
// `overrides/app/utils/segment/learning-center/eventHandlers.js` +
// `constants.js` (SFCC-11509 / ARTICLES analytics). Each emitter routes through
// the shared `trackSegment` ready-gate (nothing fires before the Segment SDK is
// loaded; WDH has no write key so the SDK never inits and these no-op there)
// and appends the `device` + `sales_channel` trailer that mirrors
// <SegmentIdentify /> and the page-level events.
//
// These are plain functions (not hooks) called from the pure data mutation in
// `use-learning-center-mutation.ts`, so the site id is resolved client-side via
// `getSiteId()` rather than `useSite()` — keeping the call-site signatures
// unchanged.
//
// `View Learning Center` is intentionally NOT emitted: it is defined-only /
// never tracked in the composable, so porting an emitter for it would introduce
// a phantom event.
//
// Hazard: goal/level/module/lesson ids are OPAQUE hierarchical keys — never the
// raw email. Identity on the wire is the ambient hashed Segment userId only
// (set by <SegmentIdentify />); these payloads carry no PII.
import { getDeviceType, getSiteId, trackSegment } from "~/segment/utils";

export const learningCenterEventNames = {
  goalProgressStatus: "Goal Progress Status",
  levelProgressStatus: "Level Progress Status",
  moduleProgressStatus: "Module Progress Status",
  lessonProgressStatus: "Lesson Progress Status",
  quizAttemptStatus: "Quiz Attempt Status",
} as const;

export type LearningCenterEventName =
  (typeof learningCenterEventNames)[keyof typeof learningCenterEventNames];

/** Progress event name → the `<type>`-prefix used in its Segment props. */
const eventNamesTypeMap: Record<LearningCenterEventName, string> = {
  [learningCenterEventNames.goalProgressStatus]: "goal",
  [learningCenterEventNames.levelProgressStatus]: "level",
  [learningCenterEventNames.moduleProgressStatus]: "module",
  [learningCenterEventNames.lessonProgressStatus]: "lesson",
  // Quiz Attempt Status has a bespoke payload shape (no `<type>Name` prefix);
  // it never resolves through this map but is listed for exhaustiveness.
  [learningCenterEventNames.quizAttemptStatus]: "quiz",
};

interface ProgressEvent {
  eventType: LearningCenterEventName;
  id: string;
  openedTimestamp?: string;
  startedTimestamp?: string;
  completedTimestamp?: string;
}

interface QuizAttemptEvent {
  quizName: string;
  quizResult: "Pass" | "Fail";
  attemptTimestamp: string;
}

/** Shared `device` + `sales_channel` trailer for every LC progress payload. */
const trailer = (): { device: string; sales_channel: string } => ({
  device: getDeviceType(),
  sales_channel: getSiteId(),
});

export const trackItemOpened = ({
  eventType,
  id,
  openedTimestamp,
}: ProgressEvent): void => {
  const type = eventNamesTypeMap[eventType];
  void trackSegment(eventType, {
    [`${type}Name`]: id,
    [`${type}Opened`]: "Yes",
    [`${type}OpenedTimestamp`]: openedTimestamp,
    ...trailer(),
  });
};

export const trackItemStarted = ({
  eventType,
  id,
  startedTimestamp,
}: ProgressEvent): void => {
  const type = eventNamesTypeMap[eventType];
  void trackSegment(eventType, {
    [`${type}Name`]: id,
    [`${type}Started`]: "Yes",
    [`${type}StartedTimestamp`]: startedTimestamp,
    ...trailer(),
  });
};

export const trackItemCompleted = ({
  eventType,
  id,
  completedTimestamp,
}: ProgressEvent): void => {
  const type = eventNamesTypeMap[eventType];
  void trackSegment(eventType, {
    [`${type}Name`]: id,
    [`${type}Completed`]: "Yes",
    [`${type}CompletedTimestamp`]: completedTimestamp,
    ...trailer(),
  });
};

export const trackQuizAttempt = ({
  quizName,
  quizResult,
  attemptTimestamp,
}: QuizAttemptEvent): void => {
  void trackSegment(learningCenterEventNames.quizAttemptStatus, {
    quizName,
    quizAttemptResult: quizResult,
    quizAttemptTimestamp: attemptTimestamp,
    ...trailer(),
  });
};
